import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { CreditCard } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loading } from "@/components/common/Loading";
import { FeatureStatusIcon, FeatureLabelBadge } from "@/components/pricing/featureBits";
import { useAuth } from "@/context/AuthContext";
import { TIER_ORDER, tierLabel } from "@/lib/tiers";
import FoundersSection from "@/components/pricing/FoundersSection";

const money = (n) => {
  const v = Number(n || 0);
  return Number.isInteger(v) ? `$${v}` : `$${v.toFixed(2)}`;
};

const fmtDate = (ts) => {
  if (!ts) return null;
  try {
    return format(new Date(ts * 1000), "MMMM d, yyyy");
  } catch {
    return null;
  }
};

function priceLabel(t, annual) {
  if (t.custom_price) return { big: t.custom_price, sub: "", per: "" };
  const m = Number(t.price_monthly || 0);
  const pma = Number(t.price_monthly_annual || 0);
  if (m === 0 && pma === 0) return { big: "Free", sub: "Forever — no card required", per: "" };
  if (annual) return { big: money(pma), per: "/mo", sub: `${money(t.price_annual)}/yr billed annually` };
  return { big: money(m), per: "/mo", sub: "Billed monthly" };
}

function PlanCard({ t, annual, currentTier, billing, busy, onUpgrade, onDowngrade, onSwitchInterval }) {
  const interval = annual ? "year" : "month";
  const curLevel = TIER_ORDER[currentTier] ?? 0;
  const level = TIER_ORDER[t.tier_key] ?? 0;
  const isCurrent = t.tier_key === currentTier;
  const isContact = !!t.custom_price;
  const isPaid = t.tier_key === "pro" || t.tier_key === "premium";
  const hasSub = !!billing?.has_subscription;
  const curInterval = billing?.interval;
  const isBusy = busy === t.tier_key;
  const p = priceLabel(t, annual);
  const features = t.features || [];

  let action;
  if (isCurrent) {
    if (isPaid && hasSub && curInterval && curInterval !== interval) {
      action = (
        <Button
          className="w-full"
          disabled={isBusy}
          onClick={() => onSwitchInterval(t.tier_key, interval)}
          data-testid={`plan-switch-interval-${t.tier_key}`}
        >
          {isBusy ? "Working…" : `Switch to ${interval === "year" ? "annual" : "monthly"} billing`}
        </Button>
      );
    } else {
      action = (
        <Button className="w-full" disabled data-testid={`plan-current-${t.tier_key}`}>Current plan</Button>
      );
    }
  } else if (isContact) {
    action = (
      <Link to="/enterprise" className="block w-full">
        <Button variant="secondary" className="w-full" data-testid={`plan-contact-${t.tier_key}`}>Contact us</Button>
      </Link>
    );
  } else if (level > curLevel) {
    action = (
      <Button
        className="w-full"
        disabled={isBusy}
        onClick={() => onUpgrade(t.tier_key, interval, hasSub)}
        data-testid={`plan-upgrade-${t.tier_key}`}
      >
        {isBusy ? "Working…" : "Upgrade"}
      </Button>
    );
  } else {
    action = (
      <Button
        variant="secondary"
        className="w-full"
        disabled={isBusy}
        onClick={() => onDowngrade(t.tier_key, interval)}
        data-testid={`plan-downgrade-${t.tier_key}`}
      >
        {isBusy ? "Working…" : "Downgrade"}
      </Button>
    );
  }

  return (
    <div
      className={`relative flex flex-col rounded-2xl border bg-card p-6 transition-shadow ${
        isCurrent ? "border-primary ring-2 ring-primary/60 shadow-md" : "border-border shadow-sm hover:shadow-md"
      }`}
      data-testid={`plan-card-${t.tier_key}`}
    >
      {isCurrent && (
        <span className="absolute -top-3 left-6 rounded-full bg-primary px-3 py-1 text-xs font-[600] text-primary-foreground" data-testid={`plan-current-badge-${t.tier_key}`}>
          Current plan
        </span>
      )}
      <h3 className="text-lg font-[640] text-foreground">{t.name || tierLabel(t.tier_key)}</h3>
      {t.subtitle && <p className="mt-1 text-sm text-muted-foreground">{t.subtitle}</p>}

      <div className="mt-4 flex items-end gap-1">
        <span className="text-3xl font-[680] tracking-tight text-foreground">{p.big}</span>
        {p.per && <span className="pb-1 text-sm text-muted-foreground">{p.per}</span>}
      </div>
      <p className="mt-1 min-h-[18px] text-xs text-muted-foreground">{p.sub}</p>

      <div className="mt-5">{action}</div>

      {features.length > 0 && (
        <ul className="mt-6 space-y-2.5 border-t border-border pt-5">
          {features.filter((f) => f.visible !== false).map((f, i) => (
            <li key={f.id || i} className="flex items-start gap-2 text-sm text-foreground" data-testid={`plan-feature-${t.tier_key}-${i}`}>
              <FeatureStatusIcon status={f.status} className="mt-0.5" />
              <span className={f.status === "not_included" ? "text-muted-foreground line-through" : ""}>
                {f.name}<FeatureLabelBadge label={f.label} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ScheduledBanner({ billing }) {
  const renewDate = fmtDate(billing?.current_period_end);
  const cancelDate = fmtDate(billing?.cancel_at) || renewDate;
  const scheduledDate = fmtDate(billing?.scheduled_effective);
  if (billing?.cancel_at_period_end && cancelDate) {
    return (
      <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="plans-cancel-banner">
        Your subscription is set to end on <span className="font-[600]">{cancelDate}</span> — you'll move to the free VIP plan then.
      </div>
    );
  }
  if (billing?.scheduled_tier && scheduledDate) {
    return (
      <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="plans-scheduled-banner">
        Scheduled change: you'll move to <span className="font-[600]">{tierLabel(billing.scheduled_tier)}</span> on <span className="font-[600]">{scheduledDate}</span>.
      </div>
    );
  }
  return null;
}

export default function Plans() {
  const { user, refreshUser } = useAuth();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [annual, setAnnual] = useState(true);
  const [busy, setBusy] = useState(null);
  const [pending, setPending] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["pricing"],
    queryFn: async () => (await api.get("/pricing")).data,
  });
  const { data: billing } = useQuery({
    queryKey: ["billing-status"],
    queryFn: async () => (await api.get("/billing/status")).data,
  });

  const refresh = async () => {
    await refreshUser();
    await qc.invalidateQueries({ queryKey: ["billing-status"] });
  };

  // Handle the return from hosted Stripe Checkout / upgrade confirmation.
  useEffect(() => {
    const checkout = params.get("checkout");
    const upgrade = params.get("upgrade");
    if (!checkout && !upgrade) return;
    const sid = params.get("session_id");
    (async () => {
      if (checkout === "success" && sid) {
        try {
          await api.post("/billing/checkout/status", { session_id: sid });
          toast.success("Subscription active — welcome to your new plan!");
          await refreshUser();
          qc.invalidateQueries({ queryKey: ["billing-status"] });
        } catch {
          toast.error("We couldn't confirm your checkout yet. Refresh in a moment.");
        }
      } else if (checkout === "cancel") {
        toast.info("Checkout canceled — no changes were made.");
      } else if (upgrade === "done") {
        toast.success("Upgrade confirmed — your new plan is active.");
        await refreshUser();
        qc.invalidateQueries({ queryKey: ["billing-status"] });
      }
      params.delete("checkout");
      params.delete("session_id");
      params.delete("upgrade");
      setParams(params, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Plan-change buttons open a confirmation dialog first (no one-click changes).
  const onUpgrade = (tier_key, interval, hasSub) => setPending({ kind: "upgrade", tier_key, interval, hasSub });
  const onDowngrade = (tier_key, interval) => setPending({ kind: "downgrade", tier_key, interval, hasSub: true });
  const onSwitchInterval = (tier_key, interval) => setPending({ kind: "switch", tier_key, interval, hasSub: true });

  const upgradeLike = pending && (pending.kind === "upgrade" || (pending.kind === "switch" && pending.interval === "year"));

  const executeChange = async () => {
    if (!pending) return;
    const { kind, tier_key, interval, hasSub } = pending;
    setBusy(tier_key);
    try {
      if (upgradeLike && !hasSub) {
        // First-time subscription -> hosted Stripe Checkout.
        const { data: res } = await api.post("/billing/checkout", { tier_key, interval, origin: window.location.origin });
        window.location.href = res.url;
        return;
      }
      const { data: res } = await api.post("/billing/change", { tier_key, interval, origin: window.location.origin });
      if (res.redirect_url) {
        // Upgrade -> Stripe hosted confirmation page (shows charges, allows card change).
        window.location.href = res.redirect_url;
        return;
      }
      if (tier_key === "standard") {
        toast.success("Cancellation scheduled — you keep full access until the end of your billing period.");
      } else if (kind === "switch") {
        toast.success("Billing switch scheduled for the end of your current period.");
      } else {
        toast.success(`Downgrade to ${tierLabel(tier_key)} scheduled for the end of your billing period.`);
      }
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't process your request.");
    } finally {
      setBusy(null);
      setPending(null);
    }
  };

  if (isLoading) return <Loading />;

  const tiers = data?.tiers || [];
  const currentTier = billing?.tier || user?.tier || "standard";

  const pendingTier = pending ? tiers.find((t) => t.tier_key === pending.tier_key) : null;
  const pendingPrice = pendingTier ? priceLabel(pendingTier, pending.interval === "year") : null;
  const dialogCopy = () => {
    if (!pending) return {};
    const name = tierLabel(pending.tier_key);
    const per = pending.interval === "year" ? "Annual" : "Monthly";
    const priceStr = pendingPrice ? `${pendingPrice.big}${pendingPrice.per || ""}${pendingPrice.sub ? ` · ${pendingPrice.sub}` : ""}` : "";
    if (pending.kind === "upgrade") {
      return {
        title: "Ready to upgrade?",
        desc: `You're upgrading to ${name} (${per}${priceStr ? ` — ${priceStr}` : ""}). You'll be taken to Stripe to review the exact charges and confirm — you can update your card there too. Upgrades take effect immediately.`,
        cta: "Continue to Stripe",
      };
    }
    if (pending.kind === "switch" && pending.interval === "year") {
      return { title: "Switch to annual billing?", desc: `You'll be taken to Stripe to review the prorated charge and confirm switching ${name} to annual billing${priceStr ? ` (${priceStr})` : ""}.`, cta: "Continue to Stripe" };
    }
    if (pending.kind === "switch") {
      return { title: "Switch to monthly billing?", desc: "Your billing switches to monthly at the end of your current period. No refund or proration applies.", cta: "Schedule switch" };
    }
    if (pending.tier_key === "standard") {
      return { title: "Cancel your subscription?", desc: `Your ${tierLabel(currentTier)} plan stays active until the end of your current billing period, then you'll move to VIP (free). No refund or proration. There are no contracts — you can re-subscribe anytime.`, cta: "Schedule cancellation" };
    }
    return { title: "Confirm downgrade", desc: `Your plan changes to ${name} at the end of your current billing period — you keep your current features until then. No refund or proration applies.`, cta: "Schedule downgrade" };
  };
  const copy = dialogCopy();

  return (
    <div className="pb-16" data-testid="plans-page">
      <div className="mb-2">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-[680] tracking-tight text-foreground sm:text-3xl">Plans</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              You're currently on the{" "}
              <span className="font-[620] text-foreground" data-testid="plans-current-tier">{tierLabel(currentTier)}</span>{" "}
              plan. Upgrade, downgrade, or explore Founders memberships below.
            </p>
          </div>
          <Link to="/dashboard/billing" className="shrink-0">
            <Button variant="secondary" size="sm" data-testid="plans-manage-billing">
              <CreditCard className="mr-1.5 h-4 w-4" /> Manage billing
            </Button>
          </Link>
        </div>
        <ScheduledBanner billing={billing} />
      </div>

      {/* Founders memberships (visibility is admin-controlled) */}
      <div className="-mx-6 mt-6 sm:-mx-8">
        <FoundersSection />
      </div>

      {/* Standard tiers */}
      <div className="mt-10">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-[640] text-foreground">Membership tiers</h2>
          <div className="flex items-center gap-3" data-testid="plans-billing-toggle">
            <span className={`text-sm ${!annual ? "font-[620] text-foreground" : "text-muted-foreground"}`}>Monthly</span>
            <Switch checked={annual} onCheckedChange={setAnnual} data-testid="plans-annual-switch" />
            <span className={`text-sm ${annual ? "font-[620] text-foreground" : "text-muted-foreground"}`}>
              Annual <span className="text-primary">(save more)</span>
            </span>
          </div>
        </div>

        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {tiers.map((t) => (
            <PlanCard
              key={t.tier_key}
              t={t}
              annual={annual}
              currentTier={currentTier}
              billing={billing}
              busy={busy}
              onUpgrade={onUpgrade}
              onDowngrade={onDowngrade}
              onSwitchInterval={onSwitchInterval}
            />
          ))}
        </div>

        <p className="mt-6 text-xs text-muted-foreground">
          Upgrades take effect immediately with a prorated charge. Downgrades apply at the end of your current
          billing period. Manage your payment method, invoices, and cancellation from{" "}
          <Link to="/dashboard/billing" className="text-primary hover:underline" data-testid="plans-billing-link">Billing</Link>.
        </p>
      </div>

      <AlertDialog open={!!pending} onOpenChange={(o) => { if (!o && !busy) setPending(null); }}>
        <AlertDialogContent data-testid="plan-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="plan-confirm-title">{copy.title}</AlertDialogTitle>
            <AlertDialogDescription data-testid="plan-confirm-desc">{copy.desc}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!busy} data-testid="plan-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); executeChange(); }}
              disabled={!!busy}
              data-testid="plan-confirm-change"
            >
              {busy ? "Working…" : (copy.cta || "Confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
